import {Howl, Howler} from 'howler';

// AudioPlayer handling class
export class AudioPlayer {

  constructor() {
    this.audios = [];
  }

  /**
   * @param {string} filename Path to the audio file.
   * @param {number} volume Float between 0 and 1.
   * @param {boolean} forcePlay If true, audio will play even if another instance of the same file is currently playing.
   * @param {function|null} callback Function to call at the end of the playback.
   * @param {boolean} triggerLastClickUpdate If true, reset app `lastClick`, to avoid still there overlay while audio is playing.
   * @param {number} rate Playback rate (affects both speed and pitch). 1.0 is normal, <1 lower/slower, >1 higher/faster.
   */
  play(filename, volume, forcePlay, callback = null, triggerLastClickUpdate = false, rate = 1.0) {
    volume = typeof volume !== "undefined" ? volume : 1.0;
    forcePlay = typeof forcePlay !== "undefined" ? forcePlay : false;

    // First check if the sound is already playing so we do not start it again
    const isPlaying = this.isPlaying(filename);

    if (isPlaying && !forcePlay) {
      console.log("Track already playing and no force play defined", filename, this.audios);
      return;
    }

    const randID = window.guid();

    // var audio = document.createElement('audio');
    // $(audio).attr("id", randID);
    // $(audio).attr("preload", "none");

    // add the audio to the body so we can safely delete it later on
    // $("body").append(audio);
    //
    // audio.src = filename;
    //
    // $(audio).one("ended", $.proxy(function () {
    // 	if (typeof this.audios[0].callback == "function") {
    // 		this.audios[0].callback();
    // 	}
    // 	this.clearTrack(randID);
    // }, this));
    //
    // $(audio).off("timeupdate").on("timeupdate", $.proxy(function () {
    // 	if (this.audios[0].triggerLastClickUpdate == true) {
    // 		app.resetTimer();
    // 	}
    // }, this));
    //
    // audio.play();
    //
    //
    // this.audios.push({
    // 	el: audio,
    // 	id: randID,
    // 	filename: filename,
    // 	callback: callback,
    // 	triggerLastClickUpdate: triggerLastClickUpdate,
    // });

    const sound = new Howl({
      src: [filename],
      volume: volume,
      rate: rate,
      // html5: true
    });

    sound.once("end", () => {
      const track = this.getTrack(randID);
      if (track && typeof track.callback == "function") {
        track.callback();
      }
      this.clearTrack(randID);
    });

    const interval = setInterval(() => {
      if (triggerLastClickUpdate) {
        if (sound.playing()) {
          app.resetTimer();
        }
      }
    }, 1000)

    sound.play();

    // console.log(sound)

    this.audios.push({
      el: sound,
      id: randID,
      filename: filename,
      callback: callback,
      interval: interval,
      triggerLastClickUpdate: triggerLastClickUpdate,
    });

    return this.audios[this.audios.length - 1];
  }

  /**
   * Plays a sound on a loop until stop() is called with its filename. Calling it
   * again while the loop is playing does nothing, and while it is fading out it
   * starts over.
   * @param {string} filename Path to the audio file.
   * @param {number} volume Float between 0 and 1.
   * @param {number} fadeInSec Seconds to fade in from silence, 0 starts at full volume.
   */
  playLoop(filename, volume = 1.0, fadeInSec = 0) {
    const current = this.getTrackByFilename(filename);
    if (current && !current.stopping) return current;
    if (current) this.clearTrack(current.id);

    const sound = new Howl({
      src: [filename],
      volume: fadeInSec > 0 ? 0 : volume,
      loop: true
    });

    sound.play();
    if (fadeInSec > 0) sound.fade(0, volume, fadeInSec * 1000);
    this.audios.push({
      volume: volume,
      paused: false,
      pauseTimeout: null,
      el: sound,
      id: window.guid(),
      filename: filename,
      callback: null,
      interval: null,
      triggerLastClickUpdate: false,
      stopping: false,
      stopTimeout: null
    });

    return this.audios[this.audios.length - 1];
  }

  /**
   * Stops every track playing that file, looping or not.
   * @param {string} filename Path to the audio file.
   * @param {number} fadeOutSec Seconds to fade out over, 0 cuts it right away.
   */
  stop(filename, fadeOutSec = 0) {
    this.audios
      .filter((track) => track.filename == filename)
      .forEach((track) => {
        if (fadeOutSec <= 0) {
          this.clearTrack(track.id);
          return;
        }
        if (track.stopping) return;

        // The fade itself is Howler's, the track is let go of once it is done.
        track.stopping = true;
        track.el.fade(track.el.volume(), 0, fadeOutSec * 1000);
        track.stopTimeout = setTimeout(() => this.clearTrack(track.id), fadeOutSec * 1000);
      });
  }

  /**
   * Holds every loop playing that file where it is, resume picks it back up.
   * @param {string} filename Path to the audio file.
   * @param {number} fadeOutSec Seconds to fade out over before holding it.
   */
  pause(filename, fadeOutSec = 0) {
    this.audios
      .filter((track) => track.filename == filename && !track.paused)
      .forEach((track) => {
        track.paused = true;
        clearTimeout(track.pauseTimeout);

        if (fadeOutSec <= 0) {
          track.el.pause();
          return;
        }

        track.el.fade(track.el.volume(), 0, fadeOutSec * 1000);
        track.pauseTimeout = setTimeout(() => track.el.pause(), fadeOutSec * 1000);
      });
  }

  /**
   * Plays a paused loop again, back up to the volume it was started at. Also
   * catches a pause still fading out.
   * @param {string} filename Path to the audio file.
   * @param {number} fadeInSec Seconds to fade back in over.
   */
  resume(filename, fadeInSec = 0) {
    this.audios
      .filter((track) => track.filename == filename && track.paused)
      .forEach((track) => {
        track.paused = false;
        clearTimeout(track.pauseTimeout);

        if (!track.el.playing()) track.el.play();

        // A loop that was on its way out when it was paused goes on fading to
        // silence, its stop is still due.
        if (track.stopping) return;

        if (fadeInSec <= 0) {
          track.el.volume(track.volume);
          return;
        }

        track.el.fade(track.el.volume(), track.volume, fadeInSec * 1000);
      });
  }

  getTrack(id) {
    return this.audios.find((track) => track.id == id) || null;
  }

  getTrackByFilename(filename) {
    return this.audios.find((track) => track.filename == filename) || null;
  }

  clearTrack(id) {
    for (let i = 0; i < this.audios.length; i++) {

      if (this.audios[i].id == id) {

        // var $el = $("#" + id);
        // $el.attr("src", "");
        // $el.get(0).load();
        // $el.off().remove();

        clearInterval(this.audios[i].interval);
        clearTimeout(this.audios[i].stopTimeout);
        clearTimeout(this.audios[i].pauseTimeout);
        this.audios[i].el.unload();

        // console.log("Track cleared", id, this.audios[i].filename);
        this.audios[i].id = null;
        this.audios[i].el = null;
        this.audios[i].filename = null;
        this.audios[i].callback = null;
        this.audios.splice(i, 1);
        break;
      }
    }
  }

  clearAll(id) {
    for (let i = 0; i < this.audios.length; i++) {
      this.clearTrack(this.audios[i].id)
    }
  }

  isPlaying(filename) {
    let playing = false;

    for (let i = 0; i < this.audios.length; i++) {
      if (this.audios[i].filename == filename) {
        playing = true;
        break;
      }
    }

    return playing;
  };

  preload(audioFiles, callback) {
    return new Promise((resolve, reject) => {
      let loaded = 0;

      if (audioFiles.length === 0) {
        // No files to load
        resolve();

      } else {
        for (let i in audioFiles) {
          const audio = new Audio();
          audio.preload = "auto";
          audio.muted = "muted";
          // audio.autobuffer = "auto";

          // once this file loads,
          // the file will be kept by the browser as cache
          audio.addEventListener('canplaythrough', function () {
            // this will be called every time an audio file is loaded
            // we keep track of the loaded files vs the requested files
            loaded++;
            // console.log("playthrough", loaded, audioFiles.length)
            if (loaded === audioFiles.length) {
              // all have loaded
              resolve();
            }
          }, false);

          audio.addEventListener('error', function (e) {
            reject(new Error(`Could not load ${audioFiles[i]}`));
          });

          audio.src = audioFiles[i];
          audio.load();
        }
      }
    })
  }
}
